export const DEFAULT_DESCRIPTION_MAX_CHARS = 1000

const GENERIC_DESCRIPTION_VALUES = new Set([
  'file',
  'upload',
  'uploaded file',
  'document',
  'uploaded document',
  'doc',
  'note',
  'content',
  'artifact',
  'site',
  'website',
  'html',
  'zip',
  'image',
  'video',
  'audio',
  'media',
  'data',
  'test',
  'description',
  'summary',
])

export type DescriptionValidationResult =
  | { ok: true; value: string }
  | { ok: false; error: string }

export interface DescriptionValidationOptions {
  fieldName?: string
  maxChars?: number
}

export function normalizeDescription(value: string): string {
  return value
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isGenericDescription(value: string): boolean {
  const normalized = normalizeDescription(value)
    .toLowerCase()
    .replace(/[.!?]+$/g, '')
  return GENERIC_DESCRIPTION_VALUES.has(normalized)
}

export function validateRequiredDescription(
  value: unknown,
  options: DescriptionValidationOptions = {},
): DescriptionValidationResult {
  const fieldName = options.fieldName ?? 'description'
  const maxChars = options.maxChars ?? DEFAULT_DESCRIPTION_MAX_CHARS

  if (typeof value !== 'string') {
    return { ok: false, error: `${fieldName} is required` }
  }

  const normalized = normalizeDescription(value)
  if (!normalized) {
    return { ok: false, error: `${fieldName} is required` }
  }

  if (normalized.length > maxChars) {
    return { ok: false, error: `${fieldName} must be ${maxChars} characters or fewer` }
  }

  if (isGenericDescription(normalized)) {
    return { ok: false, error: `${fieldName} must be specific, not a generic placeholder` }
  }

  return { ok: true, value: normalized }
}

export function assertValidDescription(
  value: unknown,
  options: DescriptionValidationOptions = {},
): string {
  const validation = validateRequiredDescription(value, options)
  if (!validation.ok) throw new Error(validation.error)
  return validation.value
}
